import React, { useCallback, useMemo } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { SubmitButton } from '@app/core';

import {
  ErrorMsg,
  useArrayMaxMsg,
  useArrayMinMsg,
  useRequiredMsg,
} from '@app/form';
import { MultipleChoice } from '../Fields';
import {
  CustomSelectChoice,
  FieldAnswer,
  hasCustomSelectChoice,
  MultipleChoiceField,
} from '@app/api';
import { SubmitType } from '../fieldConfigurations';

export const NONE_CHOICE = 'NONE_CHOICE';

interface Props {
  field: MultipleChoiceField;
  onSubmit: (values: FieldAnswer, helpers: FormikHelpers<FieldAnswer>) => any;
  submitType?: SubmitType;
}

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    textAlign: 'center',
    marginTop: theme.marginBase * 7,
  },
}));

const emptyChoices = { labels: [], refs: [] };

export const buildMultipleChoicesFieldValue = (
  field: MultipleChoiceField,
): FieldAnswer => {
  return {
    type: 'choices',
    field: {
      id: field.id,
      type: field.type,
      ref: field.ref,
      title: field.title,
    },
    choices: emptyChoices,
  };
};

export const MultipleChoiceFieldForm = ({
  field,
  onSubmit,
  submitType = SubmitType.DONE,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const options = field.properties.choices || [];

  const requiredMsg = useRequiredMsg();
  const arrayMinMsg = useArrayMinMsg(field?.validation?.minSelection || 0);
  const arrayMaxMsg = useArrayMaxMsg(
    field?.validation?.maxSelection || options.length,
  );

  const initialValues = useMemo(
    () => buildMultipleChoicesFieldValue(field),
    [field],
  );

  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        choices: Yup.object()
          .test('required', requiredMsg, (choices: any) => {
            if (!field.validation || !field.validation.required) {
              return true;
            }
            return !!(choices.labels.length && choices.refs.length);
          })
          .test('min-array', arrayMinMsg, (choices: any) => {
            if (!field.validation || !field.validation.minSelection) {
              return true;
            }
            if (hasCustomSelectChoice(choices.refs, CustomSelectChoice.None)) {
              return true;
            }
            return choices.refs.length >= field.validation.minSelection;
          })
          .test('max-array', arrayMaxMsg, (choices: any) => {
            if (!field.validation || !field.validation.maxSelection) {
              return true;
            }
            return choices.refs.length <= field.validation.maxSelection;
          })
          .test('none-only', t('screeningForm.noneError'), (choices: any) => {
            return !(
              hasCustomSelectChoice(choices.refs, CustomSelectChoice.None) &&
              hasCustomSelectChoice(choices.refs, CustomSelectChoice.Other)
            );
          }),
      }),
    [field, t, options.length, requiredMsg, arrayMinMsg, arrayMaxMsg],
  );

  const submit = useCallback(
    (values: FieldAnswer, helpers: FormikHelpers<FieldAnswer>) => {
      let metadata: any[] = [];
      if (values.choices?.metadata) {
        metadata = values.choices.metadata.filter((meta) => {
          if (!meta) {
            return false;
          }
          if (
            typeof meta === 'object' &&
            Object.keys(meta).length === 0 &&
            meta.constructor === Object
          ) {
            return false;
          }
          return true;
        });
      }

      const answer: FieldAnswer = {
        ...values,
        choices: values.choices
          ? {
              ...values.choices,
              metadata: metadata.length ? metadata : undefined,
            }
          : undefined,
      };

      onSubmit(answer, helpers);
    },
    [onSubmit],
  );

  return (
    <Formik
      onSubmit={submit}
      validationSchema={validationSchema}
      initialValues={initialValues}
      key={field.id}
    >
      {({ values }) => (
        <Form>
          <MultipleChoice field={field} />
          <ErrorMsg name="choices" />
          {submitType === SubmitType.CONTINUE ? (
            <div className={classes.button}>
              <SubmitButton
                withNextArrow
                trackingName="Screener Next"
                fullMobileWidth
                disabled={
                  field.validation &&
                  field.validation.required &&
                  (!values.choices || !values.choices.labels.length)
                }
              >
                <Trans i18nKey={'screeningForm.continue'} />
              </SubmitButton>
            </div>
          ) : (
            <div className={classes.button}>
              <SubmitButton
                color="green"
                trackingName="Screener Finished"
                fullMobileWidth
                disabled={
                  field.validation &&
                  field.validation.required &&
                  (!values.choices || !values.choices.labels.length)
                }
              >
                <Trans i18nKey={'screeningForm.end'} />
              </SubmitButton>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
